import { lazy, Suspense } from 'react';

import { Switch, Link } from 'react-router-dom';

import { isProd } from '@m/config/FeatureFlags';
import { useAdminFeatureFlag } from '@m/hooks/data/featureFlags';
import { hasRoles } from '@m/services/keycloak/iamRoles';
import { useSuperAdminCheck } from '@m/services/keycloak/useSuperAdminCheck';
import { RoleName } from '@m/types/api/iam/Iam';
import { ListPageWrapper } from 'apps/admin/AdminApp.styled';
import AuthenticatedRoute from 'apps/admin/components/AuthenticatedRoute';

const NotificationContainer = lazy(() => import('apps/admin/containers/NotificationContainer'));
const PaymentCenterContainer = lazy(
  () => import('apps/admin/containers/PaymentCenterContainer/PaymentCenterContainer'),
);
const PaymentDetailView = lazy(
  () => import('apps/admin/containers/PaymentCenterContainer/PaymentDetailView'),
);
const BulkEntSubCreateContainer = lazy(
  () => import('apps/admin/containers/BulkEntSubCreateContainer'),
);
const BulkEntSubCreateContainerV2 = lazy(
  () => import('apps/admin/containers/BulkEntSubCreateContainer/BulkEntSubCreateContainerV2'),
);
const VisitChargeManagementPage = lazy(
  () => import('apps/admin/containers/VisitChargeManagementPage'),
);
const ManagerCleanupPage = lazy(() => import('apps/admin/containers/ManagerCleanupPage'));
const HackathonToolingPage = lazy(
  () => import('apps/admin/containers/HackathonToolingPage/HackathonToolingPage'),
);
const BulkCloseViolationsPage = lazy(
  () => import('apps/admin/containers/BulkCloseViolationsPage/BulkCloseViolationsPage'),
);
const QsrDevToolsPage = lazy(() => import('apps/admin/containers/QsrDevToolsPage/QsrDevToolsPage'));

export const devToolRoutesConfig = [
  {
    exact: true,
    path: '/notification-center',
    breadcrumb: 'Notifications',
    roles: [RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <NotificationContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/payment-center',
    breadcrumb: 'Payment Center',
    roles: [
      RoleName.SYSTEM_ADMIN,
      RoleName.SUPER_ADMIN,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.LEADER,
      RoleName.MANAGER,
      RoleName.CUSTOMER_SUPPORT,
    ],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <PaymentCenterContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/payment-center/:customerId',
    breadcrumb: ({ match: { params } }: { match: { params: { customerId: string } } }) => (
      <Link to={`/customers/${params.customerId}`}>{params.customerId}</Link>
    ),
    roles: [
      RoleName.SYSTEM_ADMIN,
      RoleName.SUPER_ADMIN,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.LEADER,
      RoleName.MANAGER,
      RoleName.CUSTOMER_SUPPORT,
    ],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <PaymentDetailView />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/visit-charge-management',
    breadcrumb: 'Visit Charge Management',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <VisitChargeManagementPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/bulk-creator',
    breadcrumb: 'Create Bulk Enterprises/Subscriptions',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <BulkEntSubCreateContainer />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/enterprise-subscription-tooling',
    breadcrumb: 'Enterprise and Subscription Tooling',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <BulkEntSubCreateContainerV2 />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/manager-cleanup',
    breadcrumb: 'Manager app cleanup',
    roles: [RoleName.SUPER_ADMIN],
    component: !isProd ? AuthenticatedRoute : null,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <ManagerCleanupPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/prospect-tooling',
    breadcrumb: 'Scouted Location Tooling',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <HackathonToolingPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/bulk-close-violations',
    breadcrumb: 'Bulk Close Violations',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <ListPageWrapper>
          <BulkCloseViolationsPage />
        </ListPageWrapper>
      </Suspense>
    ),
  },
  {
    exact: true,
    path: '/qsr-dev-tools',
    breadcrumb: 'QSR Dev Tools',
    roles: [RoleName.SUPER_ADMIN],
    component: AuthenticatedRoute,
    children: (
      <Suspense fallback={null}>
        <QsrDevToolsPage />
      </Suspense>
    ),
  },
];

export default function DevToolRoutes() {
  const isSuperAdmin = useSuperAdminCheck();
  const { data: paymentCenterEnabled } = useAdminFeatureFlag('PaymentCenterView');

  return (
    <Switch>
      {devToolRoutesConfig.map(({ component: Component, breadcrumb, roles, ...props }) => {
        // Skip payment center routes if feature flag is disabled
        if (props.path?.includes('/payment-center') && !paymentCenterEnabled) {
          return null;
        }

        // If the route specifies roles, check those roles; otherwise require super admin
        const hasAccess = roles ? hasRoles(roles) : isSuperAdmin;

        return hasAccess && Component ? <Component key={props.path} {...props} /> : null;
      })}
    </Switch>
  );
}
